import { useState } from "react";
import { Clock, Loader2, MapPin, MessageCircle, Phone } from "lucide-react";
import { BUSINESS, SERVICES } from "@/lib/site";
import { Seo } from "@/components/Seo";
import { breadcrumbJsonLd } from "@/components/Breadcrumb";
import { PageHero } from "@/components/PageHero";
import { MapEmbed } from "@/components/MapEmbed";
import { Reveal } from "@/components/Reveal";

const BREADCRUMB = [
  { label: "Home", to: "/" },
  { label: "Contact", to: "/contact" },
];

const API = process.env.REACT_APP_BACKEND_URL;
const SERVICE_OPTIONS = [...SERVICES.map((s) => s.title), "Not sure / other"];
const EMPTY = { name: "", phone: "", postcode: "", service: "", message: "" };

const inputClass =
  "mt-2 w-full rounded-btn border border-line bg-white px-4 py-3 text-base text-navy transition-colors duration-200 placeholder:text-body/60 focus:border-brand focus:outline-none";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.postcode.trim() || !form.service) {
      setStatus("invalid");
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("done");
      setForm(EMPTY);
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Get a Free Quote | DMS Home Improvements"
        description="Contact DMS Home Improvements for a free site visit and fixed written quote. Family-run builders and plasterers in Molesey, Kingston and Surrey. Call, WhatsApp or send an enquiry."
        jsonLd={breadcrumbJsonLd(BREADCRUMB)}
      />
      <PageHero
        eyebrow="Contact"
        h1="Get a Free Quote"
        intro="Tell us a little about your project and we will arrange a free site visit at a time that suits you. You will receive a fixed written quote — no obligation, no deposit."
        breadcrumb={BREADCRUMB}
      />

      <section data-testid="contact-content" className="bg-white pb-16 md:pb-24">
        <div className="container-site grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
          <Reveal>
            {status === "done" ? (
              <div data-testid="contact-thankyou" className="card flex min-h-[320px] flex-col items-start justify-center p-8">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand text-white">
                  <MessageCircle size={24} aria-hidden="true" />
                </span>
                <h2 className="mt-5 text-2xl">Thanks, we&rsquo;ll be in touch shortly.</h2>
                <p className="mt-3 text-base text-body">
                  Your enquiry has come through to us. If it is urgent, call {BUSINESS.founderFirstName} on{" "}
                  <a href={BUSINESS.phoneHref} className="font-semibold text-brand hover:text-brand-dark">
                    {BUSINESS.phone}
                  </a>
                  .
                </p>
                <button
                  type="button"
                  data-testid="contact-reset-button"
                  onClick={() => setStatus("idle")}
                  className="btn btn-outline mt-8"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} noValidate className="card p-6 md:p-8">
                <div className="grid gap-5 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="text-sm font-semibold text-navy">Name</span>
                    <input data-testid="contact-name" type="text" required value={form.name} onChange={set("name")} autoComplete="name" className={inputClass} placeholder="Your name" />
                  </label>
                  <label className="block">
                    <span className="text-sm font-semibold text-navy">Phone</span>
                    <input data-testid="contact-phone" type="tel" required value={form.phone} onChange={set("phone")} autoComplete="tel" className={inputClass} placeholder="Best number to reach you" />
                  </label>
                  <label className="block">
                    <span className="text-sm font-semibold text-navy">Postcode</span>
                    <input data-testid="contact-postcode" type="text" required value={form.postcode} onChange={set("postcode")} autoComplete="postal-code" className={inputClass} placeholder="e.g. KT8" />
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-sm font-semibold text-navy">Service</span>
                    <select data-testid="contact-service" required value={form.service} onChange={set("service")} className={inputClass}>
                      <option value="" disabled>
                        Choose the type of work
                      </option>
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-sm font-semibold text-navy">Message</span>
                    <textarea data-testid="contact-message" rows={4} value={form.message} onChange={set("message")} className={inputClass} placeholder="Tell us a little about the job (optional)" />
                  </label>
                </div>

                {status === "invalid" && (
                  <p data-testid="contact-invalid" className="mt-4 text-sm font-medium text-destructive">
                    Please add your name, phone, postcode and the type of work, then try again.
                  </p>
                )}
                {status === "error" && (
                  <p data-testid="contact-error" className="mt-4 text-sm font-medium text-destructive">
                    Something went wrong sending your enquiry. Please try again, or call {BUSINESS.founderFirstName} on {BUSINESS.phone}.
                  </p>
                )}

                <button type="submit" data-testid="contact-submit" disabled={status === "sending"} className="btn btn-brand mt-6 w-full disabled:opacity-70 sm:w-auto">
                  {status === "sending" ? (
                    <>
                      <Loader2 size={18} className="animate-spin" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    "Send My Enquiry"
                  )}
                </button>
                <p className="mt-4 text-xs text-body">No email address needed. We only use your details to get back to you about your enquiry.</p>
              </form>
            )}
          </Reveal>

          <Reveal delay={80}>
            <div data-testid="contact-trust-panel" className="flex flex-col gap-6">
              <div className="card p-6">
                <h2 className="text-xl">Speak To Us Directly</h2>
                <ul className="mt-5 space-y-4 text-base text-body">
                  <li className="flex items-center gap-3">
                    <Phone size={18} className="shrink-0 text-brand" aria-hidden="true" />
                    <a href={BUSINESS.phoneHref} data-testid="contact-phone-link" className="font-semibold text-navy transition-colors hover:text-brand">
                      {BUSINESS.phone}
                    </a>
                  </li>
                  <li className="flex items-center gap-3">
                    <MessageCircle size={18} className="shrink-0 text-brand" aria-hidden="true" />
                    <a href={BUSINESS.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="transition-colors hover:text-navy">
                      Message us on WhatsApp
                    </a>
                  </li>
                  <li className="flex items-start gap-3">
                    <Clock size={18} className="mt-1 shrink-0 text-brand" aria-hidden="true" />
                    <span data-testid="contact-hours">
                      Opening hours: <span className="font-semibold text-navy">[CONFIRM WITH DAVE]</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin size={18} className="mt-1 shrink-0 text-brand" aria-hidden="true" />
                    <span>Based in {BUSINESS.address.locality}, {BUSINESS.address.region}</span>
                  </li>
                </ul>
              </div>
              <MapEmbed title="Map showing DMS Home Improvements based in Molesey" query="Molesey, Surrey, UK" zoom={13} className="h-[280px]" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
